// SpecGroup.jsx
// ─────────────────────────────────────────────────────────────
// Groups POs by specification (exact match).
// For each spec-group shows:
//   1. The PO table for the active year
//   2. Cross-year price history panel — SAME SPEC ONLY
//      (e.g. "Intel i5 Laptop" in 2025 vs 2026 — NOT compared with i7)
// ─────────────────────────────────────────────────────────────
import { useMemo, useState } from "react";
import styles from "./SourcingIntelligence.module.css";
import { Icons, parseINR } from "./SourcingIntelligence";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Divider from "@mui/material/Divider";

// ── Delivery chip ─────────────────────────────────────────────
function DeliveryChip({ status, days }) {
  const cfg = {
    on_time: { cls: styles.deliveryOnTime, icon: <Icons.CheckCircle size={13} />, label: "On Time" },
    overdue:  { cls: styles.deliveryOverdue, icon: <Icons.AlertCircle size={13} />, label: "Overdue" },
    unknown:  { cls: styles.deliveryUnknown, icon: <Icons.HelpCircle size={13} />, label: "Unknown" },
  }[status] || { cls: styles.deliveryUnknown, icon: <Icons.HelpCircle size={13} />, label: "Unknown" };

  return (
    <div>
      <span className={`${styles.deliveryChip} ${cfg.cls}`}>
        {cfg.icon}&nbsp;{cfg.label}
      </span>
      {days !== null && days !== undefined && (
        <div className={styles.deliveryDays}>
          <Icons.Clock size={12} />
          {days > 0
            ? `${days} day${days !== 1 ? "s" : ""} remaining`
            : days < 0
            ? `${Math.abs(days)} day${Math.abs(days) !== 1 ? "s" : ""} overdue`
            : "Due today"}
        </div>
      )}
    </div>
  );
}

// ── Price comparison box (vs same PO's previous purchase) ─────
function PriceCompBox({ comp }) {
  if (!comp || comp.price_direction === "no previous data") {
    return (
      <span className={styles.priceCompNoData}>
        <Icons.Info size={13} /> No Prior Data
      </span>
    );
  }

  const dir = comp.price_direction;
  const pct = comp.unit_price_difference_percentage;
  const dirClass = dir === "increased" ? styles.dirIncreased
                 : dir === "decreased" ? styles.dirDecreased
                 : styles.dirSame;
  const dirIcon  = dir === "increased" ? <Icons.TrendUp size={12} />
                 : dir === "decreased" ? <Icons.TrendDown size={12} />
                 : <Icons.Equal size={12} />;

  return (
    <div className={styles.priceCompBox}>
      <div className={styles.priceCompRow}>
        <span className={styles.priceCompLabel}>Prev PO#</span>
        <span className={styles.priceCompVal} style={{ color: "var(--primary)" }}>{comp.previous_po_number}</span>
      </div>
      <div className={styles.priceCompRow}>
        <span className={styles.priceCompLabel}>Prev Date</span>
        <span className={styles.priceCompVal}>{comp.previous_po_date || "—"}</span>
      </div>
      <div className={styles.priceCompRow}>
        <span className={styles.priceCompLabel}>Prev Unit Price</span>
        <span className={styles.priceCompVal}>₹{comp.previous_unit_price}</span>
      </div>
      <hr className={styles.priceCompDivider} />
      <div>
        <span className={`${styles.priceDirectionChip} ${dirClass}`}>
          {dirIcon}&nbsp;
          {pct !== null ? `${pct > 0 ? "+" : ""}${pct?.toFixed(2)}%` : "—"}&nbsp;
          {dir.charAt(0).toUpperCase() + dir.slice(1)}
        </span>
        {comp.unit_price_difference && (
          <div className={styles.priceCompDiff}>
            Difference: ₹{comp.unit_price_difference}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Cross-year same-spec price history ────────────────────────
// This is the KEY module: for spec S, collect all POs across ALL years
// that share the EXACT same specification string, then display them
// as a timeline: Year → Unit Price → Vendor → PO# → change %
function SpecCrossYearHistory({ spec, allYears, currentYear }) {
  const timeline = useMemo(() => {
    const out = [];
    [...(allYears || [])]
      .sort((a, b) => Number(a.year) - Number(b.year))
      .forEach((y) => {
        (y.itemwise || []).forEach((item) => {
          if (item.specification === spec) out.push({ ...item, year: y.year });
        });
      });
    return out;
  }, [allYears, spec]);

  if (timeline.length < 2) {
    return (
      <div className={styles.specHistoryPanel}>
        <div className={styles.specHistoryTitle}>
          <Icons.History size={13} />
          Cross-Year Price History (Same Spec)
        </div>
        <span className={`${styles.badge} ${styles.badgeNeutral}`}>
          Only 1 purchase found for this exact specification — no cross-year comparison available.
        </span>
      </div>
    );
  }

  return (
    <div className={styles.specHistoryPanel}>
      <div className={styles.specHistoryTitle}>
        <Icons.History size={13} />
        Cross-Year Price History — Same Specification Only
        <span className={`${styles.badge} ${styles.badgePrimary}`} style={{ fontSize: 11 }}>
          {timeline.length} purchases across {[...new Set(timeline.map(t => t.year))].length} years
        </span>
      </div>

      <div className={styles.specHistoryHint}>
        Only compares the <strong>same specification text</strong> across years. The % badge compares vs the previous purchase event of this same spec.
      </div>

      <div className={styles.specHistoryCardsTitle}>Purchase Events (same spec)</div>
      <div className={styles.specHistoryGrid}>
        {timeline.map((entry, idx) => {
          const prev = idx > 0 ? timeline[idx - 1] : null;
          const prevPrice = prev ? parseINR(prev.unit_price) : null;
          const currPrice = parseINR(entry.unit_price);
          const pct = prevPrice ? ((currPrice - prevPrice) / prevPrice) * 100 : null;
          const isUp   = pct !== null && pct > 0;
          const isDown = pct !== null && pct < 0;
          const isFlat = pct !== null && pct === 0;
          const isCurrent = entry.year === currentYear;

          return (
            <div key={`${entry.year}-${entry.po_number}`} style={{ display: "flex", alignItems: "center", gap: 0 }}>
              {idx > 0 && (
                <div className={styles.specHistoryArrow}>
                  <Icons.ArrowRight size={16} />
                </div>
              )}
              <div
                className={styles.specHistoryItem}
                style={{
                  borderColor: isCurrent ? "var(--primary)" : "var(--border)",
                  background: isCurrent ? "linear-gradient(160deg,#EAF2FF,#fff)" : "#fff",
                  boxShadow: isCurrent ? "0 4px 16px rgba(21,101,192,0.12)" : "none",
                }}
              >
                {/* Change badge relative to previous PO of same spec */}
                {pct !== null && (
                  <span
                    className={`${styles.specHistoryChangeBadge} ${
                      isUp ? styles.dirIncreased : isDown ? styles.dirDecreased : styles.dirSame
                    }`}
                  >
                    {isUp ? "+" : ""}{pct.toFixed(1)}%
                  </span>
                )}

                <div className={styles.specHistoryItemYear}>
                  FY {entry.year} {isCurrent ? "· Current" : ""}
                </div>
                <div className={styles.specHistoryItemPrice}>
                  ₹{entry.unit_price}
                  <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text-sub)", marginLeft: 4 }}>
                    / unit
                  </span>
                </div>
                <div style={{ fontSize: 12, color: "var(--text-sub)", fontWeight: 600, marginTop: 4 }}>
                  Quantity: {entry.quantity} · Total: ₹{entry.total_price}
                </div>
                <div className={styles.specHistoryItemVendor} title={entry.vendor_name}>
                  {entry.vendor_name}
                </div>
                <div className={styles.specHistoryItemPo}>
                  PO# {entry.po_number} · {entry.po_date}
                </div>
                {/* Price direction vs previous in same spec */}
                {prev && (
                  <div style={{ marginTop: 8, fontSize: 12 }}>
                    <span style={{
                      display: "inline-flex", alignItems: "center", gap: 4,
                      padding: "2px 8px", borderRadius: 20, fontSize: 11, fontWeight: 700,
                      background: isUp ? "var(--danger-bg)" : isDown ? "var(--success-bg)" : "var(--neutral-bg)",
                      color: isUp ? "var(--danger)" : isDown ? "var(--success)" : "var(--neutral)",
                    }}>
                      {isUp ? <Icons.TrendUp size={11} /> : isDown ? <Icons.TrendDown size={11} /> : <Icons.Equal size={11} />}
                      vs prev: ₹{prev.unit_price} ({pct >= 0 ? "+" : ""}{pct?.toFixed(1)}%)
                    </span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary row: min/max price for this spec */}
      {timeline.length > 1 && (() => {
        const prices = timeline.map(t => parseINR(t.unit_price));
        const minP = Math.min(...prices);
        const maxP = Math.max(...prices);
        const minEntry = timeline.find(t => parseINR(t.unit_price) === minP);
        const maxEntry = timeline.find(t => parseINR(t.unit_price) === maxP);
        const swing = ((maxP - minP) / minP * 100).toFixed(1);
        return (
          <div style={{
            marginTop: 16,
            padding: "12px 16px",
            background: "var(--white)",
            border: "1.5px solid var(--border)",
            borderRadius: "var(--radius-sm)",
            display: "flex",
            gap: 24,
            flexWrap: "wrap",
            fontSize: 13,
            fontWeight: 600,
          }}>
            <div>
              <span style={{ color: "var(--text-muted)" }}>Lowest Price (Same Spec): </span>
              <strong style={{ color: "var(--success)" }}>₹{minEntry?.unit_price}</strong>
              <span style={{ color: "var(--text-muted)" }}> · {minEntry?.vendor_name} · FY {minEntry?.year}</span>
            </div>
            <div>
              <span style={{ color: "var(--text-muted)" }}>Highest Price (Same Spec): </span>
              <strong style={{ color: "var(--danger)" }}>₹{maxEntry?.unit_price}</strong>
              <span style={{ color: "var(--text-muted)" }}> · {maxEntry?.vendor_name} · FY {maxEntry?.year}</span>
            </div>
            <div>
              <span style={{ color: "var(--text-muted)" }}>Price Swing: </span>
              <strong style={{ color: "var(--warning)" }}>{swing}%</strong>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ── Main SpecGroup component ──────────────────────────────────
export default function SpecGroup({ yearData, allYears, activeYear }) {
  const [openSpec, setOpenSpec] = useState(null);

  const specGroups = useMemo(() => {
    const groups = {};
    (yearData?.itemwise || []).forEach((item) => {
      const key = item?.specification || "—";
      if (!groups[key]) groups[key] = [];
      groups[key].push(item);
    });
    return groups;
  }, [yearData]);

  const openRows = openSpec ? specGroups[openSpec] || [] : [];
  const openTotals = useMemo(() => {
    const total = openRows.reduce((s, r) => s + parseINR(r.total_price), 0);
    const qty = openRows.reduce((s, r) => s + Number(r.quantity || 0), 0);
    const vendors = [...new Set(openRows.map((r) => r.vendor_name).filter(Boolean))].length;
    const overdue = openRows.reduce((s, r) => s + (r.delivery_status === "overdue" ? 1 : 0), 0);
    return { total, qty, vendors, overdue };
  }, [openRows]);

  return (
    <>
      {Object.entries(specGroups).map(([spec, rows], gi) => {
        const groupTotal = rows.reduce((s, r) => s + parseINR(r.total_price), 0);
        const groupQty = rows.reduce((s, r) => s + r.quantity, 0);

        // Delivery breakdown for this spec group
        const delivCounts = rows.reduce((acc, r) => {
          acc[r.delivery_status] = (acc[r.delivery_status] || 0) + 1;
          return acc;
        }, {});

        return (
          <div key={spec} className={styles.specGroupWrap}>
            {/* Spec Group Header */}
            <div
              className={styles.specGroupHeader}
            >
              <div className={styles.specGroupHeaderLeft}>
                <div className={styles.specGroupIcon}>
                  <Icons.Package size={16} />
                </div>
                <div className={styles.specGroupInfo}>
                  <div className={styles.specGroupLabel}>
                    Specification {gi + 1}
                    &nbsp;·&nbsp;
                    <span className={`${styles.badge} ${styles.badgePrimary}`} style={{ fontSize: 11 }}>
                      {rows.length} PO{rows.length !== 1 ? "s" : ""}
                    </span>
                    {delivCounts["overdue"] > 0 && (
                      <span className={`${styles.badge} ${styles.badgeDanger}`} style={{ fontSize: 11, marginLeft: 6 }}>
                        {delivCounts["overdue"]} overdue
                      </span>
                    )}
                  </div>
                  <div className={styles.specGroupText} title={spec}>{spec}</div>
                </div>
              </div>
              <div className={styles.specGroupHeaderRight}>
                <div style={{ textAlign: "right" }}>
                  <div className={styles.specGroupTotal}>₹{Number(groupTotal).toLocaleString("en-IN")}</div>
                  <div style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 600 }}>
                    Quantity: {groupQty}
                  </div>
                </div>
                <button
                  type="button"
                  className={styles.specGroupViewBtn}
                  onClick={() => setOpenSpec(spec)}
                >
                  View Transaction Details
                </button>
              </div>
            </div>
          </div>
        );
      })}

      <Dialog
        open={Boolean(openSpec)}
        onClose={() => setOpenSpec(null)}
        maxWidth="xl"
        fullWidth
        PaperProps={{ className: styles.specDialogPaper }}
      >
        <div className={styles.specDialogHeader}>
          <div className={styles.specDialogHeaderLeft}>
            <div className={styles.specDialogEyebrow}>Specification Details</div>
            <div className={styles.specDialogSpec} title={openSpec || ""}>{openSpec}</div>
            <div className={styles.specDialogMeta}>
              FY {activeYear} · {openRows.length} PO{openRows.length !== 1 ? "s" : ""} · Vendors {openTotals.vendors} · Quantity {Number(openTotals.qty || 0).toLocaleString("en-IN")}
              {openTotals.overdue > 0 ? ` · Overdue ${openTotals.overdue}` : ""}
            </div>
          </div>
          <div className={styles.specDialogHeaderRight}>
            <div className={styles.specDialogTotal}>₹{Number(openTotals.total || 0).toLocaleString("en-IN")}</div>
            <IconButton className={styles.specDialogClose} onClick={() => setOpenSpec(null)} aria-label="Close">
              <span className={styles.specDialogCloseX}>×</span>
            </IconButton>
          </div>
        </div>
        <Divider />
        <DialogContent className={styles.specDialogContent}>
          <div className={styles.specDialogSectionTitle}>PO Details (FY {activeYear})</div>
          <div className={styles.tableScroll}>
            <table className={styles.poTable}>
              <thead>
                <tr>
                  <th className={styles.th} style={{ width: 36 }}>#</th>
                  <th className={styles.th}>PO ID</th>
                  <th className={styles.th}>PO Date</th>
                  <th className={styles.th}>Vendor</th>
                  <th className={styles.th} style={{ textAlign: "center" }}>Quantity</th>
                  <th className={styles.th}>Unit Price</th>
                  <th className={styles.th}>Total Price</th>
                  <th className={styles.th}>Currency</th>
                  <th className={styles.th}>Expected Delivery</th>
                  <th className={styles.th}>Delivery Status</th>
                  <th className={styles.th}>
                    vs Previous PO
                    <div style={{ fontSize: 11, textTransform: "none", letterSpacing: 0, color: "var(--text-muted)", fontWeight: 600 }}>
                      (Same spec only)
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {openRows.map((row, ri) => (
                  <tr key={`${row.po_number}-${ri}`}>
                    <td className={styles.td} style={{ color: "var(--text-muted)", fontSize: 13, fontWeight: 700 }}>
                      {ri + 1}
                    </td>
                    <td className={styles.td}>
                      <span className={styles.poNumber}>{row.po_number}</span>
                    </td>
                    <td className={styles.td}>
                      <div className={styles.dateCell}>
                        <Icons.Calendar size={13} />
                        {row.po_date}
                      </div>
                    </td>
                    <td className={styles.td}>
                      <div className={styles.vendorCell} title={row.vendor_name}>
                        <Icons.Building size={13} />
                        <span className={styles.vendorName}>{row.vendor_name}</span>
                      </div>
                    </td>
                    <td className={styles.td} style={{ textAlign: "center", fontWeight: 800, fontSize: 15 }}>
                      {row.quantity}
                    </td>
                    <td className={styles.td}>
                      <span className={styles.unitPrice}>₹{row.unit_price}</span>
                    </td>
                    <td className={styles.td}>
                      <span className={styles.totalPrice}>₹{row.total_price}</span>
                    </td>
                    <td className={styles.td}>
                      <span className={`${styles.badge} ${styles.badgeNeutral}`}>{row.currency}</span>
                    </td>
                    <td className={styles.td}>
                      {row.expected_delivery_date ? (
                        <div className={styles.dateCell}>
                          <Icons.Calendar size={13} />
                          {row.expected_delivery_date}
                        </div>
                      ) : (
                        <span style={{ color: "var(--text-muted)" }}>—</span>
                      )}
                    </td>
                    <td className={styles.td}>
                      <DeliveryChip status={row.delivery_status} days={row.remaining_delivery_days} />
                    </td>
                    <td className={styles.td}>
                      <PriceCompBox comp={row.last_purchase_comparison} />
                    </td>
                  </tr>
                ))}

                <tr className={styles.subtotalRow}>
                  <td className={styles.td} colSpan={4} style={{ fontSize: 13 }}>
                    <span style={{ color: "var(--text-sub)" }}>
                      Subtotal — {openRows.length} PO{openRows.length !== 1 ? "s" : ""}
                    </span>
                  </td>
                  <td className={styles.td} style={{ textAlign: "center", fontSize: 15 }}>
                    {openTotals.qty}
                  </td>
                  <td className={styles.td} />
                  <td className={styles.td} style={{ color: "var(--primary)", fontSize: 16 }}>
                    ₹{Number(openTotals.total || 0).toLocaleString("en-IN")}
                  </td>
                  <td className={styles.td} colSpan={4} />
                </tr>
              </tbody>
            </table>
          </div>

          <SpecCrossYearHistory spec={openSpec || ""} allYears={allYears} currentYear={activeYear} />
        </DialogContent>
      </Dialog>
    </>
  );
}
